/**
 * Per-user persistence of the active brand id.
 *
 * Keyed by userId so two users sharing the same browser each keep their own
 * remembered brand, and re-login restores whichever brand the user was last
 * working on. Lives in localStorage (not the auth helper's dual storage):
 * the brand selection is a user preference, not a credential, so it should
 * survive even "Remember Me = off" sessions.
 */

const ACTIVE_BRAND_KEY = 'activeBrandIdByUser';

type BrandMap = Record<string, number>;

const readMap = (): BrandMap => {
    try {
        const raw = localStorage.getItem(ACTIVE_BRAND_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed as BrandMap : {};
    } catch {
        return {};
    }
};

export const getStoredActiveBrandId = (userId: number | string | null | undefined): number | null => {
    if (userId == null) return null;
    const map = readMap();
    const value = map[String(userId)];
    return typeof value === 'number' ? value : null;
};

export const setStoredActiveBrandId = (userId: number | string | null | undefined, brandId: number | null): void => {
    if (userId == null) return;
    const map = readMap();
    if (brandId == null) {
        delete map[String(userId)];
    } else {
        map[String(userId)] = brandId;
    }
    localStorage.setItem(ACTIVE_BRAND_KEY, JSON.stringify(map));
};
